import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthCard } from '../components/AuthCard';
import { Alert } from '../components/Alert';
import { Field } from '../components/Field';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { CompteBetaAvis } from '../components/CompteBetaAvis';

/** Ce que l'API rend avant la suppression (cf. `SuppressionCompteService.apercu`). */
interface ApercuSuppression {
  email: string;
  possible: boolean;
  blocage: { code: 'PROPRIETE_A_TRANSFERER' | 'COMPTE_ADMINISTRATEUR'; message: string } | null;
  espace: {
    nom: string;
    role: 'OWNER' | 'ADMIN' | 'MEMBER';
    autresMembres: number;
    issue: 'QUITTER' | 'FERMER' | 'TRANSFERT_REQUIS';
  } | null;
  abonnements: { produit: string; fin: string }[];
  reconnexionRequise: boolean;
  /** Délai de réflexion (02/10/2026), et date de la suppression si elle est demandée maintenant. */
  delaiJours: number;
  suppressionLe: string;
}

/** Réponse à la demande (cf. `SuppressionCompteService.supprimer`). */
interface ResultatDemande {
  programmee: boolean;
  suppressionLe: string | null;
}

const CONFIRMATION = 'SUPPRIMER';
const ICI = '/compte/suppression';

/** « 1er novembre 2026 », et non « 1 novembre 2026 ». */
const date = (iso: string) =>
  new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    .format(new Date(iso))
    .replace(/^1 /, '1er ');

/**
 * Supprimer son compte SEmply depuis le portail (01/10/2026).
 *
 * Google Play exige que le compte puisse être supprimé SANS l'appli : la page
 * publique semply.fr/suppression-compte renvoie ici. Mêmes règles que dans
 * l'appli — connexion de moins de dix minutes, propriété d'un espace partagé à
 * transférer d'abord —, appliquées par l'API ; cette page montre ce qui va se
 * passer, puis demande de taper SUPPRIMER.
 *
 * Depuis le 02/10/2026, la demande ouvre un délai de réflexion de trente
 * jours : le compte est déconnecté de partout, et supprimé à l'échéance. Se
 * reconnecter d'ici là annule tout — la page le dit avant ET après.
 */
export function SuppressionComptePage() {
  const navigate = useNavigate();
  const { user, loading, fetchMe, logout, setUser } = useAuthStore();
  const [apercu, setApercu] = useState<ApercuSuppression | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [supprime, setSupprime] = useState<{ email: string; le: string | null } | null>(null);

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (loading || supprime) return;
    // Pas de session : connexion, puis retour ICI (liste fermée du portail).
    if (!user) {
      navigate(`/login?${new URLSearchParams({ suite: ICI })}`);
      return;
    }
    let vivant = true;
    api
      .get<ApercuSuppression>('/auth/suppression')
      .then((a) => {
        if (vivant) setApercu(a);
      })
      .catch((err: unknown) => {
        if (vivant) setErreur(err instanceof ApiError ? err.message : 'Portail injoignable.');
      });
    return () => {
      vivant = false;
    };
  }, [loading, user, supprime, navigate]);

  /** Reconnexion : on ferme la session, et la connexion ramène ici. */
  async function seReconnecter() {
    await logout();
    navigate(`/login?${new URLSearchParams({ suite: ICI })}`);
  }

  async function supprimer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saisie.trim() !== CONFIRMATION || envoi || !apercu) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const resultat = await api.post<ResultatDemande>('/auth/suppression', {
        confirmation: CONFIRMATION,
      });
      setSupprime({ email: apercu.email, le: resultat.suppressionLe });
      // La session n'existe plus : rien du compte ne doit rester affiché.
      setUser(null);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'server_error';
      if (code === 'REAUTHENTIFICATION_REQUISE') {
        setApercu({ ...apercu, reconnexionRequise: true });
        return;
      }
      setErreur(
        err instanceof ApiError ? err.message : 'La suppression n’a pas abouti. Réessayez.',
      );
    } finally {
      setEnvoi(false);
    }
  }

  if (supprime) {
    return (
      <AuthCard
        title={supprime.le ? 'Suppression programmée' : 'Compte supprimé'}
        subtitle={supprime.email}
      >
        {supprime.le ? (
          <>
            <p>
              Votre compte SEmply sera supprimé le <strong>{date(supprime.le)}</strong>. Vous êtes
              déconnecté de tous vos appareils, et un e-mail de confirmation vient de vous être
              envoyé.
            </p>
            <p>
              Vous avez changé d’avis ? Reconnectez-vous avant cette date : la suppression sera
              annulée et votre compte restera tel quel.
            </p>
            <div className="links">
              <Link to="/login">Me reconnecter</Link>
            </div>
          </>
        ) : (
          <p>Votre compte SEmply a été supprimé.</p>
        )}
        <p>
          <a href="https://semply.fr">Retour sur semply.fr</a>
        </p>
      </AuthCard>
    );
  }

  if (loading || !user || (!apercu && !erreur)) {
    return <AuthCard title="Chargement…">{null}</AuthCard>;
  }

  if (!apercu) {
    return (
      <AuthCard title="Supprimer mon compte">
        <Alert message={erreur} />
        <div className="links">
          <Link to="/compte">Retour à mon compte</Link>
        </div>
      </AuthCard>
    );
  }

  const espace = apercu.espace;

  return (
    <AuthCard title="Supprimer mon compte" subtitle={apercu.email}>
      <Alert message={erreur} />

      <p>
        Votre compte sera supprimé définitivement dans {apercu.delaiJours} jours, le{' '}
        <strong>{date(apercu.suppressionLe)}</strong>, sur toute la suite SEmply. Dès votre demande,
        vous êtes déconnecté de tous vos appareils ; d’ici là, il vous suffit de vous reconnecter
        pour annuler.
      </p>
      <ul
        style={{
          fontSize: '0.9rem',
          lineHeight: 1.6,
          paddingLeft: '1.2rem',
          // Puces retirées par la charte (`li { list-style: none }`) : l'écart
          // sépare les points.
          display: 'grid',
          gap: '0.5rem',
        }}
      >
        <li>
          Effacés à cette date : votre nom, votre adresse e-mail, votre mot de passe, vos connexions
          Google, Apple ou Microsoft et votre double authentification.
        </li>
        {espace?.issue === 'QUITTER' ? (
          <li>Vous quitterez l’espace « {espace.nom} », qui continuera sans vous.</li>
        ) : null}
        {espace?.issue === 'FERMER' ? (
          <li>
            L’espace « {espace.nom} » sera fermé à cette date. Les données de ses dossiers
            (factures, comptabilité…) sont ensuite conservées pendant la durée imposée par la loi —
            dix ans pour les pièces comptables —, puis effacées.
          </li>
        ) : null}
        {apercu.abonnements.length > 0 ? (
          <li>
            Abonnements arrêtés dès votre demande, sans renouvellement :{' '}
            {apercu.abonnements.map((a) => `${a.produit} (jusqu’au ${date(a.fin)})`).join(', ')}.
            Ils reprennent si vous annulez.
          </li>
        ) : null}
      </ul>

      {user.betaCompte ? (
        // Compte bêta partagé (03/10/2026) : la suppression est refusée par l'API.
        <CompteBetaAvis />
      ) : apercu.blocage ? (
        <>
          <Alert message={apercu.blocage.message} />
          {apercu.blocage.code === 'PROPRIETE_A_TRANSFERER' ? (
            <div className="links">
              <Link to="/espace">Désigner un nouveau propriétaire</Link>
            </div>
          ) : null}
        </>
      ) : apercu.reconnexionRequise ? (
        <>
          <p>
            Pour confirmer qu’il s’agit bien de vous, reconnectez-vous : vous reviendrez sur cette
            page, et aurez alors dix minutes pour confirmer la suppression.
          </p>
          <button className="btn" type="button" onClick={() => void seReconnecter()}>
            Me reconnecter
          </button>
        </>
      ) : (
        <form onSubmit={(e) => void supprimer(e)}>
          <Field
            label={`Tapez ${CONFIRMATION} pour confirmer`}
            name="confirmation"
            autoComplete="off"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value.toUpperCase())}
          />
          <button
            className="btn btn-danger"
            type="submit"
            disabled={envoi || saisie.trim() !== CONFIRMATION}
          >
            {envoi ? 'Envoi…' : `Supprimer mon compte dans ${apercu.delaiJours} jours`}
          </button>
        </form>
      )}

      <div className="links">
        <Link to="/compte">Annuler</Link>
      </div>
    </AuthCard>
  );
}
